import { getCouponDisplayInfo } from '@cart-services/utils/getCouponDisplayInfo'
import { getDiscountPercentage } from '@cart-services/utils/getDiscountPercentage'
import { type AvailableCouponCriterion, type AvailableCriterionGroup, PromotionType } from '@hooks-api/types/dataModel/coupon'

const getCriterionLabelPrefix = (criterionGroup: AvailableCriterionGroup, firstCriterion: AvailableCouponCriterion, isMultipleCriterionGroup: boolean) => {
  const { promotion_type } = criterionGroup
  if (isMultipleCriterionGroup) return '跨區'
  if (promotion_type === PromotionType.DIS_SHIPPING || promotion_type === PromotionType.FREE_SHIPPING) return ''
  const { criterion_amount = null, criterion_quantity = null } = firstCriterion
  if (criterion_amount !== null && criterion_amount > 0) return '滿額'
  if (criterion_quantity !== null && criterion_quantity > 0) return '滿件'
  if (!criterion_amount && !criterion_quantity) return '下單'
  return ''
}

const getCriterionLabelSuffix = (criterionGroup: AvailableCriterionGroup) => {
  const { promotion_type } = criterionGroup
  const labelSuffixMap = {
    [PromotionType.DISCOUNT_RATE]: '折',
    [PromotionType.DISCOUNT_PRICE]: '折',
    [PromotionType.FIXED_PRICE]: '特價',
    [PromotionType.GIVEAWAY]: '送',
    [PromotionType.DIS_SHIPPING]: '運費折扣',
    [PromotionType.FREE_SHIPPING]: '運費折扣',
  } as const
  return promotion_type ? labelSuffixMap[promotion_type] || '' : ''
}

const getTitlePrefix = (criterionGroup: AvailableCriterionGroup, firstCriterion: AvailableCouponCriterion) => {
  const { promotion_type } = criterionGroup
  const { fixed_price = null, discount_amount = null, dis_shipping_amount = null } = firstCriterion
  if (promotion_type === PromotionType.DISCOUNT_PRICE && discount_amount !== null) {
    return '-$'
  }
  else if (promotion_type === PromotionType.FIXED_PRICE && fixed_price !== null) {
    return '$'
  }
  else if (promotion_type === PromotionType.DIS_SHIPPING && dis_shipping_amount !== null) {
    return '-$'
  }
  return ''
}

const getTitleSuffix = (criterionGroup: AvailableCriterionGroup, firstCriterion: AvailableCouponCriterion): {
  message: string
  value: {
    discountPercentage?: number
    discountRate?: number
    discountAmount?: number
    fixedPrice?: number
    disShippingAmount?: number
  }
} => {
  const { promotion_type } = criterionGroup
  const { discount_rate = null, fixed_price = null, discount_amount = null, dis_shipping_amount = null } = firstCriterion
  if (promotion_type === PromotionType.DISCOUNT_RATE && discount_rate !== null) {
    return {
      message: '{{discountPercentage}}折',
      value: {
        discountPercentage: getDiscountPercentage(discount_rate),
        /**
         * 英文翻譯用 eg: 5% off -> {{discountRate}}% off
         */
        discountRate: discount_rate,
      },
    }
  }
  else if (promotion_type === PromotionType.DISCOUNT_PRICE && discount_amount !== null) {
    return {
      message: '{{discountAmount}}',
      value: {
        discountAmount: discount_amount,
      },
    }
  }
  else if (promotion_type === PromotionType.FIXED_PRICE && fixed_price !== null) {
    return {
      message: '{{fixedPrice}}',
      value: {
        fixedPrice: fixed_price,
      },
    }
  }
  else if (promotion_type === PromotionType.GIVEAWAY) {
    return {
      message: '贈品',
      value: {},
    }
  }
  else if (promotion_type === PromotionType.DIS_SHIPPING && dis_shipping_amount !== null) {
    return {
      message: '{{disShippingAmount}}',
      value: {
        disShippingAmount: dis_shipping_amount,
      },
    }
  }
  else if (promotion_type === PromotionType.FREE_SHIPPING) {
    return {
      message: '免運',
      value: {},
    }
  }
  return {
    message: '',
    value: {},
  }
}

export const parseSystemDefaultMessage = (criterionGroups: AvailableCriterionGroup[],
) => {
  const displayInfo = getCouponDisplayInfo(criterionGroups)
  if (!displayInfo) {
    return {
      title: {
        message: '',
        value: {},
      },
      titlePrefix: '',
      titleSuffix: {
        message: '',
        value: {},
      },
    }
  }
  const { criterionGroup, firstCriterion, maxDiscountAmountCriterion } = displayInfo
  const titlePrefix = getTitlePrefix(criterionGroup, firstCriterion)
  const titleSuffix = getTitleSuffix(criterionGroup, maxDiscountAmountCriterion || firstCriterion)
  const criterionLabelPrefix = getCriterionLabelPrefix(criterionGroup, firstCriterion, criterionGroups.length > 1)
  const criterionLabelSuffix = getCriterionLabelSuffix(criterionGroup)
  return {
    titlePrefix,
    titleSuffix,
    subTitle: `${criterionLabelPrefix}${criterionLabelSuffix}`,
  }
}
